import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSubscription } from '../api/client'
import {
  getFridgeDevices, createFridgeDevice, updateFridgeDevice, deleteFridgeDevice,
  getFridgeLogs, createFridgeLog, deleteFridgeLog,
  getCleaningLogs, createCleaningLog, deleteCleaningLog,
  getProductionLogs, createProductionLog, deleteProductionLog,
  getSemifinished, createSemifinished, patchSemifinished, deleteSemifinished,
  getRecipes, getStaff,
  getDisplayLogs, createDisplayLog, removeDisplayLog, undoRemoveDisplayLog, deleteDisplayLog,
  getKitchenLabel, printRollLabel,
} from '../api/client'
import { Plus, Trash2, Thermometer, CheckSquare, ChefHat, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Package, X, Settings, Pencil, Lock, Zap, Store, Undo2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { hu } from 'date-fns/locale'

const today = new Date().toISOString().slice(0, 10)

type Tab = 'fridge' | 'cleaning' | 'production' | 'display'

// ── Hőmérséklet tartományok ──────────────────────────────────────────────────
// Konyhai címke nyomtatása (Brother QL 62 mm) egy naplóbejegyzésből — egy kattintás
function KitchenLabelButton({ source, id }: { source: 'production' | 'semifinished' | 'display'; id: number }) {
  const [busy, setBusy] = useState(false)
  const print = async () => {
    setBusy(true)
    try {
      const res = await getKitchenLabel(source, id)
      if (await printRollLabel(res.data) === 'cloud') toast.success('Elküldve a címkenyomtatóra')
    } catch (e: any) {
      toast.error(e?.response ? 'A címke nem készült el, próbáld újra.' : (e?.message || 'Nyomtatási hiba'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <button onClick={print} disabled={busy} title="Konyhai címke nyomtatása"
      className={`transition-colors shrink-0 ${busy ? 'text-brand-400 animate-pulse' : 'text-gray-300 hover:text-brand-600'}`}>
      <Printer className="w-4 h-4" />
    </button>
  )
}

function tempOk(temp: number | null | undefined, device: any): boolean | null {
  if (temp === null || temp === undefined) return null
  const max = device?.temp_critical_max ?? device?.temp_max ?? (device?.device_type === 'fagyaszto' ? -15 : 8)
  const min = device?.temp_critical_min ?? device?.temp_min ?? (device?.device_type === 'fagyaszto' ? -40 : 0)
  return temp >= min && temp <= max
}

function tempWarning(temp: number | null | undefined, device: any): boolean | null {
  if (temp === null || temp === undefined) return null
  if (device?.temp_max != null && device?.temp_min != null) {
    return temp >= device.temp_min && temp <= device.temp_max
  }
  return null
}

const tempColor = (ok: boolean | null) =>
  ok === null ? 'text-gray-400' : ok ? 'text-green-600' : 'text-red-500'

// ── Munkatárs kiválasztó ─────────────────────────────────────────────────────
function StaffSelect({ value, onChange, label = 'Kezelő neve' }: {
  value: string
  onChange: (name: string) => void
  label?: string
}) {
  const { data: staff = [] } = useQuery({
    queryKey: ['staff'],
    queryFn: () => getStaff().then(r => r.data),
    staleTime: 60000,
  })
  const members = staff as any[]

  return (
    <div>
      <label className="label">{label}</label>
      {members.length > 0 ? (
        <select
          className="input"
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">— Nincs megadva —</option>
          {members.map((m: any) => (
            <option key={m.id} value={m.name}>
              {m.name}{m.role ? ` (${m.role})` : ''}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Kezelő neve..."
        />
      )}
    </div>
  )
}

// ── Takarítási feladatlista ──────────────────────────────────────────────────
const DEFAULT_TASKS = [
  { task: 'Munkaterület fertőtlenítése', freq: 'daily' },
  { task: 'Kézmosó tisztítása', freq: 'daily' },
  { task: 'Padló mosása / fertőtlenítése', freq: 'daily' },
  { task: 'Eszközök mosása, fertőtlenítése', freq: 'daily' },
  { task: 'Szeméttároló ürítése', freq: 'daily' },
  { task: 'Vitrin tisztítása', freq: 'daily' },
  { task: 'Hűtőszekrény belső tisztítása', freq: 'weekly' },
  { task: 'Sütő tisztítása', freq: 'weekly' },
  { task: 'Polcok törlése', freq: 'weekly' },
  { task: 'Falak, csempék tisztítása', freq: 'weekly' },
  { task: 'Fagyasztó leolvasztása, tisztítása', freq: 'monthly' },
  { task: 'Szellőzők tisztítása', freq: 'monthly' },
  { task: 'Raktár általános takarítás', freq: 'monthly' },
]

const FREQ_LABEL: Record<string, string> = {
  daily: 'Napi',
  weekly: 'Heti',
  monthly: 'Havi',
}

const FREQ_COLOR: Record<string, string> = {
  daily: 'bg-blue-100 text-blue-700',
  weekly: 'bg-purple-100 text-purple-700',
  monthly: 'bg-orange-100 text-orange-700',
}

// ── Hűtő/Fagyasztó tab ──────────────────────────────────────────────────────
const DEVICE_PRESETS = [
  { label: 'Hűtőszekrény (standard)', type: 'huto', min: 0, max: 4, crit: 8 },
  { label: 'Vitrin hűtő', type: 'huto', min: 0, max: 5, crit: 8 },
  { label: 'Fagyasztó (standard)', type: 'fagyaszto', min: -25, max: -18, crit: -15 },
  { label: 'Mélyhűtő', type: 'fagyaszto', min: -30, max: -20, crit: -18 },
]

function DeviceManager({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [editId, setEditId] = useState<number | null>(null)
  const emptyForm = { name: '', device_type: 'huto', temp_min: '', temp_max: '', temp_critical_max: '', temp_critical_min: '', location: '' }
  const [form, setForm] = useState<any>({ ...emptyForm })

  const { data: devices = [] } = useQuery({ queryKey: ['fridge-devices'], queryFn: () => getFridgeDevices().then(r => r.data) })

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        temp_min: form.temp_min === '' ? null : +form.temp_min,
        temp_max: form.temp_max === '' ? null : +form.temp_max,
        temp_critical_max: form.temp_critical_max === '' ? null : +form.temp_critical_max,
        temp_critical_min: form.temp_critical_min === '' ? null : +form.temp_critical_min,
      }
      return editId ? updateFridgeDevice(editId, payload) : createFridgeDevice(payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fridge-devices'] })
      toast.success(editId ? 'Eszköz frissítve' : 'Eszköz hozzáadva')
      setForm({ ...emptyForm }); setEditId(null)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteFridgeDevice(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fridge-devices'] }); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f: any) => ({ ...f, [k]: e.target.value }))

  function applyPreset(preset: typeof DEVICE_PRESETS[0]) {
    setForm((f: any) => ({
      ...f,
      device_type: preset.type,
      temp_min: preset.min,
      temp_max: preset.max,
      temp_critical_max: preset.type === 'huto' ? preset.crit : '',
      temp_critical_min: preset.type === 'fagyaszto' ? preset.crit : '',
    }))
  }

  function startEdit(d: any) {
    setEditId(d.id)
    setForm({
      name: d.name, device_type: d.device_type,
      temp_min: d.temp_min ?? '', temp_max: d.temp_max ?? '',
      temp_critical_max: d.temp_critical_max ?? '', temp_critical_min: d.temp_critical_min ?? '',
      location: d.location ?? '',
    })
  }

  return (
    <div className="card p-5 mb-5 border-blue-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">Eszközök kezelése</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
      </div>

      {/* Meglévő eszközök */}
      {(devices as any[]).length > 0 && (
        <div className="mb-5 space-y-2">
          {(devices as any[]).map((d: any) => (
            <div key={d.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-800">{d.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${d.device_type === 'fagyaszto' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {d.device_type === 'fagyaszto' ? 'Fagyasztó' : 'Hűtő'}
                  </span>
                  {d.location && <span className="text-xs text-gray-400">{d.location}</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-3">
                  {d.temp_min != null && d.temp_max != null && (
                    <span>Optimális: {d.temp_min}°C – {d.temp_max}°C</span>
                  )}
                  {d.temp_critical_max != null && <span className="text-red-500">Max kritikus: {d.temp_critical_max}°C</span>}
                  {d.temp_critical_min != null && <span className="text-red-500">Min kritikus: {d.temp_critical_min}°C</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(d)} className="text-gray-400 hover:text-blue-500 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteMut.mutate(d.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Új / szerkesztés form */}
      <div className="border-t border-gray-100 pt-4">
        <div className="text-xs font-semibold text-gray-500 mb-2">{editId ? 'Eszköz szerkesztése' : 'Új eszköz hozzáadása'}</div>

        {/* Preset gombok */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {DEVICE_PRESETS.map(p => (
            <button key={p.label} onClick={() => applyPreset(p)}
              className="text-xs px-2.5 py-1 rounded-full border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-600 transition-colors">
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label">Eszköz neve *</label>
            <input className="input" value={form.name} onChange={set('name')} placeholder="pl. Raktár hűtő" />
          </div>
          <div>
            <label className="label">Típus</label>
            <select className="input" value={form.device_type} onChange={set('device_type')}>
              <option value="huto">Hűtőszekrény</option>
              <option value="fagyaszto">Fagyasztó</option>
            </select>
          </div>
          <div>
            <label className="label">Helyszín (opcionális)</label>
            <input className="input" value={form.location} onChange={set('location')} placeholder="pl. Raktár" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label">Optimális min (°C)</label>
            <input className="input" type="number" step="0.5" value={form.temp_min} onChange={set('temp_min')} placeholder="pl. 0" />
          </div>
          <div>
            <label className="label">Optimális max (°C)</label>
            <input className="input" type="number" step="0.5" value={form.temp_max} onChange={set('temp_max')} placeholder="pl. 4" />
          </div>
          {form.device_type === 'huto' && (
            <div>
              <label className="label text-red-600">Kritikus max (°C)</label>
              <input className="input border-red-200" type="number" step="0.5" value={form.temp_critical_max} onChange={set('temp_critical_max')} placeholder="pl. 8" />
            </div>
          )}
          {form.device_type === 'fagyaszto' && (
            <div>
              <label className="label text-red-600">Kritikus min (°C)</label>
              <input className="input border-red-200" type="number" step="0.5" value={form.temp_critical_min} onChange={set('temp_critical_min')} placeholder="pl. -15" />
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button className="btn-primary text-sm" onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending}>
            {editId ? 'Mentés' : 'Hozzáadás'}
          </button>
          {editId && (
            <button className="btn-secondary text-sm" onClick={() => { setEditId(null); setForm({ ...emptyForm }) }}>Mégse</button>
          )}
        </div>
      </div>
    </div>
  )
}

function FridgeTab() {
  const qc = useQueryClient()
  const [showNew, setShowNew] = useState(false)
  const [showDevices, setShowDevices] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | ''>('')
  const [form, setForm] = useState({
    log_date: today,
    device_id: null as number | null,
    device_name: '',
    device_type: 'huto',
    temp_morning: '' as string | number,
    temp_noon: '' as string | number,
    temp_evening: '' as string | number,
    operator_name: '',
    deviation: '',
    corrective_action: '',
  })

  const { data: devices = [] } = useQuery({ queryKey: ['fridge-devices'], queryFn: () => getFridgeDevices().then(r => r.data) })
  const { data: logs = [] } = useQuery({ queryKey: ['fridge-logs'], queryFn: () => getFridgeLogs().then(r => r.data) })

  const selectedDevice = (devices as any[]).find((d: any) => d.id === selectedDeviceId)

  function selectDevice(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = +e.target.value || null
    const dev = (devices as any[]).find((d: any) => d.id === id)
    setSelectedDeviceId(id || '')
    setForm(f => ({
      ...f,
      device_id: id,
      device_name: dev?.name || '',
      device_type: dev?.device_type || 'huto',
    }))
  }

  const createMut = useMutation({
    mutationFn: () => createFridgeLog({
      ...form,
      temp_morning: form.temp_morning === '' ? null : +form.temp_morning,
      temp_noon: form.temp_noon === '' ? null : +form.temp_noon,
      temp_evening: form.temp_evening === '' ? null : +form.temp_evening,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fridge-logs'] })
      toast.success('Hőmérséklet bejegyezve')
      setForm({ log_date: today, device_id: null, device_name: '', device_type: 'huto', temp_morning: '', temp_noon: '', temp_evening: '', operator_name: '', deviation: '', corrective_action: '' })
      setSelectedDeviceId('')
      setShowNew(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteFridgeLog(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fridge-logs'] }); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  const grouped = (logs as any[]).reduce((acc: any, log: any) => {
    const d = log.log_date; if (!acc[d]) acc[d] = []; acc[d].push(log); return acc
  }, {} as Record<string, any[]>)

  const deviceMap = Object.fromEntries((devices as any[]).map((d: any) => [d.id, d]))

  // Hőmérséklet ellenőrzés az eszköz konfigurált határaival
  function checkTemp(log: any, temp: number | null) {
    const dev = log.device_id ? deviceMap[log.device_id] : null
    if (dev) return tempOk(temp, dev)
    // fallback ha nincs eszköz konfig
    if (temp === null || temp === undefined) return null
    return log.device_type === 'fagyaszto' ? temp <= -15 : temp <= 8
  }

  const noDevices = (devices as any[]).length === 0

  return (
    <div>
      {/* Eszköz kezelő */}
      {showDevices ? (
        <DeviceManager onClose={() => setShowDevices(false)} />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            {noDevices ? (
              <p className="text-sm text-amber-600 font-medium flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                Még nincs eszköz beállítva — add meg a hűtőid és fagyasztóid
              </p>
            ) : (
              <p className="text-sm text-gray-500">
                {(devices as any[]).length} eszköz konfigurálva
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="btn-secondary flex items-center gap-2 text-sm" onClick={() => setShowDevices(true)}>
              <Settings className="w-4 h-4" /> Eszközök
            </button>
            <button className="btn-primary flex items-center gap-2 text-sm"
              onClick={() => setShowNew(v => !v)} disabled={noDevices}>
              <Plus className="w-4 h-4" /> Mérés rögzítése
            </button>
          </div>
        </div>
      )}

      {/* Mérés rögzítő form */}
      {showNew && !showDevices && (
        <div className="card p-5 mb-5 border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-4">Hőmérséklet mérés rögzítése</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label">Dátum *</label>
              <input className="input" type="date" value={form.log_date} onChange={set('log_date')} />
            </div>
            <div>
              <label className="label">Eszköz *</label>
              <select className="input" value={selectedDeviceId} onChange={selectDevice}>
                <option value="">— Válassz eszközt —</option>
                {(devices as any[]).map((d: any) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.device_type === 'fagyaszto' ? 'Fagyasztó' : 'Hűtő'}{d.location ? ` · ${d.location}` : ''})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedDevice && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 text-xs text-blue-700 flex items-center gap-4">
              <span>Optimális tartomány: <strong>{selectedDevice.temp_min}°C – {selectedDevice.temp_max}°C</strong></span>
              {selectedDevice.device_type === 'huto' && selectedDevice.temp_critical_max != null && (
                <span className="text-red-600">Kritikus max: <strong>{selectedDevice.temp_critical_max}°C</strong></span>
              )}
              {selectedDevice.device_type === 'fagyaszto' && selectedDevice.temp_critical_min != null && (
                <span className="text-red-600">Kritikus min: <strong>{selectedDevice.temp_critical_min}°C</strong></span>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            {[['Reggeli hőm. (°C)', 'temp_morning'], ['Déli hőm. (°C)', 'temp_noon'], ['Esti hőm. (°C)', 'temp_evening']].map(([label, key]) => {
              const val = (form as any)[key]
              const ok = selectedDevice && val !== '' ? tempOk(+val, selectedDevice) : null
              return (
                <div key={key}>
                  <label className="label">{label}</label>
                  <div className="relative">
                    <input
                      className={`input pr-8 ${ok === false ? 'border-red-400 bg-red-50' : ok === true ? 'border-green-400' : ''}`}
                      type="number" step="0.1" placeholder="—" value={val} onChange={set(key)}
                    />
                    {ok !== null && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2">
                        {ok ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <StaffSelect value={form.operator_name} onChange={v => setForm(f => ({ ...f, operator_name: v }))} />
            <div>
              <label className="label">Eltérés / megjegyzés</label>
              <input className="input" value={form.deviation} onChange={set('deviation')} />
            </div>
            {form.deviation && (
              <div className="col-span-2">
                <label className="label">Korrekciós intézkedés</label>
                <textarea className="input h-16 resize-none" value={form.corrective_action}
                  onChange={e => setForm(f => ({ ...f, corrective_action: e.target.value }))} />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => createMut.mutate()}
              disabled={!form.device_name || createMut.isPending}>Mentés</button>
            <button className="btn-secondary text-sm" onClick={() => setShowNew(false)}>Mégse</button>
          </div>
        </div>
      )}

      {/* Napló lista */}
      <div className="space-y-3">
        {Object.keys(grouped).length === 0 && (
          <p className="text-gray-400 text-sm text-center py-10">Még nincs hőmérséklet bejegyzés</p>
        )}
        {Object.entries(grouped).map(([dateStr, dayLogs]: [string, any]) => {
          const isOpen = expanded === +new Date(dateStr)
          const hasDeviation = dayLogs.some((l: any) => l.deviation)
          const hasAlert = dayLogs.some((l: any) => {
            const dev = l.device_id ? deviceMap[l.device_id] : null
            return [l.temp_morning, l.temp_noon, l.temp_evening].some(t => t != null && tempOk(t, dev || l) === false)
          })
          return (
            <div key={dateStr} className="card overflow-hidden">
              <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(isOpen ? null : +new Date(dateStr))}>
                <div className="flex items-center gap-3">
                  {hasAlert || hasDeviation
                    ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    : <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  }
                  <span className="font-medium text-gray-900 text-sm">
                    {format(new Date(dateStr), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
                  </span>
                  <span className="text-xs text-gray-400">{dayLogs.length} eszköz</span>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {dayLogs.map((log: any) => {
                    const dev = log.device_id ? deviceMap[log.device_id] : null
                    const okM = checkTemp(log, log.temp_morning)
                    const okN = checkTemp(log, log.temp_noon)
                    const okE = checkTemp(log, log.temp_evening)
                    const anyBad = okM === false || okN === false || okE === false
                    return (
                      <div key={log.id} className={`px-4 py-3 flex items-start justify-between gap-4 ${anyBad ? 'bg-red-50/50' : ''}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <Thermometer className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-medium text-sm text-gray-800">{log.device_name}</span>
                            <span className="text-xs text-gray-400">{log.device_type === 'fagyaszto' ? 'Fagyasztó' : 'Hűtő'}</span>
                            {dev && dev.temp_min != null && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                {dev.temp_min}–{dev.temp_max}°C
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm flex-wrap">
                            {log.temp_morning != null && (
                              <span className={`flex items-center gap-1 ${tempColor(okM)}`}>
                                Reggel: <strong>{log.temp_morning}°C</strong>
                                {okM === false && <AlertTriangle className="w-3 h-3" />}
                              </span>
                            )}
                            {log.temp_noon != null && (
                              <span className={`flex items-center gap-1 ${tempColor(okN)}`}>
                                Dél: <strong>{log.temp_noon}°C</strong>
                                {okN === false && <AlertTriangle className="w-3 h-3" />}
                              </span>
                            )}
                            {log.temp_evening != null && (
                              <span className={`flex items-center gap-1 ${tempColor(okE)}`}>
                                Este: <strong>{log.temp_evening}°C</strong>
                                {okE === false && <AlertTriangle className="w-3 h-3" />}
                              </span>
                            )}
                          </div>
                          {log.deviation && (
                            <div className="mt-1.5 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                              ⚠ {log.deviation}
                              {log.corrective_action && <span className="ml-2 text-gray-600">→ {log.corrective_action}</span>}
                            </div>
                          )}
                          {log.operator_name && <div className="text-xs text-gray-400 mt-1">Kezelő: {log.operator_name}</div>}
                        </div>
                        <button onClick={() => deleteMut.mutate(log.id)} className="text-gray-300 hover:text-red-500 shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Takarítási napló tab ─────────────────────────────────────────────────────
function CleaningTab() {
  const qc = useQueryClient()
  const [showNew, setShowNew] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [form, setForm] = useState({
    log_date: today,
    operator_name: '',
    tasks: DEFAULT_TASKS.map(t => ({ ...t, done: false })),
    notes: '',
  })

  const { data: logs = [] } = useQuery({
    queryKey: ['cleaning-logs'],
    queryFn: () => getCleaningLogs().then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: () => createCleaningLog(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cleaning-logs'] })
      toast.success('Takarítási napló mentve')
      setForm({ log_date: today, operator_name: '', tasks: DEFAULT_TASKS.map(t => ({ ...t, done: false })), notes: '' })
      setShowNew(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteCleaningLog(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cleaning-logs'] }); toast.success('Törölve') },
  })

  function toggleTask(idx: number) {
    setForm(f => ({
      ...f,
      tasks: f.tasks.map((t, i) => i === idx ? { ...t, done: !t.done } : t),
    }))
  }

  const freqGroups = ['daily', 'weekly', 'monthly'] as const

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Napi, heti és havi takarítási feladatok rögzítése</p>
        <button className="btn-primary flex items-center gap-2 text-sm" onClick={() => setShowNew(v => !v)}>
          <Plus className="w-4 h-4" /> Új bejegyzés
        </button>
      </div>

      {showNew && (
        <div className="card p-5 mb-5 border-green-200">
          <h3 className="font-semibold text-gray-800 mb-4">Takarítási napló bejegyzés</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="label">Dátum *</label>
              <input className="input" type="date" value={form.log_date} onChange={e => setForm(f => ({ ...f, log_date: e.target.value }))} />
            </div>
            <StaffSelect value={form.operator_name} onChange={v => setForm(f => ({ ...f, operator_name: v }))} />
          </div>

          {freqGroups.map(freq => (
            <div key={freq} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${FREQ_COLOR[freq]}`}>{FREQ_LABEL[freq]}</span>
                <span className="text-xs text-gray-400">feladatok</span>
              </div>
              <div className="space-y-1.5">
                {form.tasks.map((t, idx) => t.freq !== freq ? null : (
                  <label key={idx} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${t.done ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'}`}>
                    <input type="checkbox" className="w-4 h-4 accent-green-500" checked={t.done} onChange={() => toggleTask(idx)} />
                    <span className={`text-sm ${t.done ? 'text-green-700 line-through' : 'text-gray-700'}`}>{t.task}</span>
                    {t.done && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="mb-4">
            <label className="label">Megjegyzés</label>
            <textarea className="input h-16 resize-none" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => createMut.mutate()} disabled={createMut.isPending}>Mentés</button>
            <button className="btn-secondary text-sm" onClick={() => setShowNew(false)}>Mégse</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(logs as any[]).length === 0 && (
          <p className="text-gray-400 text-sm text-center py-10">Még nincs takarítási bejegyzés</p>
        )}
        {(logs as any[]).map((log: any) => {
          const tasks = log.tasks || []
          const done = tasks.filter((t: any) => t.done).length
          const total = tasks.length
          const pct = total > 0 ? Math.round(done / total * 100) : 0
          const isOpen = expanded === log.id
          return (
            <div key={log.id} className="card overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(isOpen ? null : log.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${pct === 100 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {pct}%
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 text-sm">
                      {format(new Date(log.log_date), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
                    </div>
                    <div className="text-xs text-gray-400">
                      {done}/{total} feladat elvégezve
                      {log.operator_name && ` · ${log.operator_name}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={e => { e.stopPropagation(); deleteMut.mutate(log.id) }} className="text-gray-300 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 px-4 py-3">
                  {freqGroups.map(freq => {
                    const group = tasks.filter((t: any) => t.freq === freq)
                    if (!group.length) return null
                    return (
                      <div key={freq} className="mb-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${FREQ_COLOR[freq]} mb-2 inline-block`}>{FREQ_LABEL[freq]}</span>
                        <div className="space-y-1 mt-1">
                          {group.map((t: any, i: number) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              {t.done
                                ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                                : <div className="w-4 h-4 rounded border-2 border-gray-300 shrink-0" />
                              }
                              <span className={t.done ? 'text-gray-500 line-through' : 'text-gray-700'}>{t.task}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                  {log.notes && <div className="text-xs text-gray-500 mt-2 bg-gray-50 rounded px-2 py-1">{log.notes}</div>}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Félkész termék helpers ───────────────────────────────────────────────────
function daysRemaining(expiryDate: string) {
  const diff = new Date(expiryDate).setHours(0,0,0,0) - new Date().setHours(0,0,0,0)
  return Math.floor(diff / 86400000)
}

function SemifinishedBadge({ days }: { days: number }) {
  if (days < 0) return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">LEJÁRT</span>
  )
  if (days === 0) return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Ma jár le</span>
  )
  if (days <= 2) return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{days} nap múlva</span>
  )
  return (
    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">{days} nap</span>
  )
}

function SemifinishedSection() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    name: '',
    quantity: '' as string | number,
    unit: 'kg',
    produced_date: today,
    expiry_date: '',
    batch_number: '',
    operator_name: '',
    notes: '',
  })

  const { data: items = [] } = useQuery({
    queryKey: ['semifinished'],
    queryFn: () => getSemifinished(true).then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: () => createSemifinished({ ...form, quantity: +form.quantity }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semifinished'] })
      toast.success('Félkész termék rögzítve')
      setForm({ name: '', quantity: '', unit: 'kg', produced_date: today, expiry_date: '', batch_number: '', operator_name: '', notes: '' })
      setShowForm(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const depleteMut = useMutation({
    mutationFn: (id: number) => patchSemifinished(id, { is_depleted: true }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['semifinished'] }); toast.success('Felhasználva / elfogyott') },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteSemifinished(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['semifinished'] }); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  const allItems = items as any[]
  const expiring = allItems.filter(i => daysRemaining(i.expiry_date) <= 2)

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-brand-600" />
          <span className="font-semibold text-gray-800 text-sm">Aktív félkész termékek</span>
          {expiring.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {expiring.length} hamarosan lejár
            </span>
          )}
        </div>
        <button
          onClick={() => setShowForm(v => !v)}
          className="text-xs font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Új félkész termék
        </button>
      </div>

      {showForm && (
        <div className="card p-4 mb-3 border-brand-200 bg-brand-50/30">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="sm:col-span-2">
              <label className="label">Termék neve *</label>
              <input className="input" value={form.name} onChange={set('name')} placeholder="pl. Málnazselé, Vaníliakrém" />
            </div>
            <div>
              <label className="label">Mennyiség *</label>
              <div className="flex gap-2">
                <input className="input" type="number" step="0.1" value={form.quantity} onChange={set('quantity')} />
                <select className="input w-24 shrink-0" value={form.unit} onChange={set('unit')}>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="l">l</option>
                  <option value="adag">adag</option>
                  <option value="db">db</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">Lejárat *</label>
              <input className="input" type="date" value={form.expiry_date} onChange={set('expiry_date')} />
            </div>
            <div>
              <label className="label">Gyártás dátuma</label>
              <input className="input" type="date" value={form.produced_date} onChange={set('produced_date')} />
            </div>
            <div>
              <label className="label">Tételszám</label>
              <input className="input" value={form.batch_number} onChange={set('batch_number')} />
            </div>
            <StaffSelect value={form.operator_name} onChange={v => setForm((f: any) => ({ ...f, operator_name: v }))} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => createMut.mutate()}
              disabled={!form.name || !form.quantity || !form.expiry_date || createMut.isPending}>
              Rögzítés
            </button>
            <button className="btn-secondary text-sm" onClick={() => setShowForm(false)}>Mégse</button>
          </div>
        </div>
      )}

      {allItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-center">
          <p className="text-sm text-gray-400">Nincs aktív félkész termék</p>
          <p className="text-xs text-gray-300 mt-1">Pl. főzött krémek, zseléalapok, tészták — eltartási idővel</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {allItems.map((item: any) => {
            const days = daysRemaining(item.expiry_date)
            const isExpired = days < 0
            const isWarning = days >= 0 && days <= 2
            return (
              <div key={item.id} className={`rounded-xl border p-3 flex items-start justify-between gap-2 ${
                isExpired ? 'border-red-200 bg-red-50' : isWarning ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'
              }`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-sm truncate">{item.name}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-sm font-semibold text-gray-700">{item.quantity} {item.unit}</span>
                    <SemifinishedBadge days={days} />
                  </div>
                  {item.batch_number && (
                    <div className="text-xs text-gray-400 font-mono mt-0.5">#{item.batch_number}</div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <KitchenLabelButton source="semifinished" id={item.id} />
                  <button
                    onClick={() => depleteMut.mutate(item.id)}
                    title="Felhasználva / elfogyott"
                    className="text-gray-300 hover:text-green-600 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteMut.mutate(item.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Termelési napló tab ──────────────────────────────────────────────────────
function ProductionTab() {
  const qc = useQueryClient()
  const [showNew, setShowNew] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [form, setForm] = useState({
    log_date: today,
    recipe_id: null as number | null,
    recipe_name: '',
    quantity: '' as string | number,
    unit: 'db',
    production_time: '',
    baking_temp: '' as string | number,
    cooling_temp: '' as string | number,
    batch_number: '',
    operator_name: '',
    notes: '',
  })

  const { data: logs = [] } = useQuery({
    queryKey: ['production-logs'],
    queryFn: () => getProductionLogs().then(r => r.data),
  })

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => getRecipes().then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: () => createProductionLog({
      ...form,
      quantity: +form.quantity || 0,
      baking_temp: form.baking_temp === '' ? null : +form.baking_temp,
      cooling_temp: form.cooling_temp === '' ? null : +form.cooling_temp,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-logs'] })
      toast.success('Termelési tétel rögzítve')
      setForm({ log_date: today, recipe_id: null, recipe_name: '', quantity: '', unit: 'db', production_time: '', baking_temp: '', cooling_temp: '', batch_number: '', operator_name: '', notes: '' })
      setShowNew(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteProductionLog(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-logs'] }); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  function selectRecipe(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = +e.target.value || null
    const recipe = (recipes as any[]).find(r => r.id === id)
    const bakingTemp = recipe?.baking_temp ? (recipe.baking_temp.match(/\d+(\.\d+)?/) || [])[0] || '' : ''
    setForm(f => ({ ...f, recipe_id: id, recipe_name: recipe?.name || '', baking_temp: bakingTemp }))
  }

  const grouped = (logs as any[]).reduce((acc: any, log: any) => {
    const d = log.log_date
    if (!acc[d]) acc[d] = []
    acc[d].push(log)
    return acc
  }, {} as Record<string, any[]>)

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Mi készült, mikor, ki által — tételszám és hőmérsékletek</p>
        <button className="btn-primary flex items-center gap-2 text-sm" onClick={() => setShowNew(v => !v)}>
          <Plus className="w-4 h-4" /> Termelési tétel
        </button>
      </div>

      {showNew && (
        <div className="card p-5 mb-5 border-brand-200">
          <h3 className="font-semibold text-gray-800 mb-4">Termelési napló bejegyzés</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label">Dátum *</label>
              <input className="input" type="date" value={form.log_date} onChange={set('log_date')} />
            </div>
            <div>
              <label className="label">Időpont</label>
              <input className="input" type="time" value={form.production_time} onChange={set('production_time')} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Termék (receptből)</label>
              <select className="input mb-2" value={form.recipe_id || ''} onChange={selectRecipe}>
                <option value="">— Válassz receptet —</option>
                {(recipes as any[]).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <input className="input" placeholder="Vagy írd be kézzel..." value={form.recipe_name}
                onChange={set('recipe_name')} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="label">Mennyiség *</label>
              <input className="input" type="number" step="0.1" value={form.quantity} onChange={set('quantity')} />
            </div>
            <div>
              <label className="label">Egység</label>
              <select className="input" value={form.unit} onChange={set('unit')}>
                <option value="db">db</option>
                <option value="kg">kg</option>
                <option value="szelet">szelet</option>
                <option value="adag">adag</option>
              </select>
            </div>
            <div>
              <label className="label">Tételszám</label>
              <input className="input" value={form.batch_number} onChange={set('batch_number')} placeholder="pl. 2026-05-01-A" />
            </div>
            <div>
              <label className="label">Sütési hőm. (°C)</label>
              <input className="input" type="number" step="1" value={form.baking_temp} onChange={set('baking_temp')} placeholder="—" />
            </div>
            <div>
              <label className="label">Hűtési hőm. (°C)</label>
              <input className="input" type="number" step="0.1" value={form.cooling_temp} onChange={set('cooling_temp')} placeholder="—" />
            </div>
            <StaffSelect value={form.operator_name} onChange={v => setForm(f => ({ ...f, operator_name: v }))} />
          </div>
          <div className="mb-4">
            <label className="label">Megjegyzés</label>
            <textarea className="input h-14 resize-none" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => createMut.mutate()}
              disabled={!form.recipe_name || !form.quantity || createMut.isPending}>Mentés</button>
            <button className="btn-secondary text-sm" onClick={() => setShowNew(false)}>Mégse</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {Object.keys(grouped).length === 0 && (
          <p className="text-gray-400 text-sm text-center py-10">Még nincs termelési bejegyzés</p>
        )}
        {Object.entries(grouped).map(([dateStr, dayLogs]: [string, any]) => {
          const isOpen = expanded === dateStr
          const totalQty = dayLogs.reduce((s: number, l: any) => s + (l.quantity || 0), 0)
          return (
            <div key={dateStr} className="card overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                onClick={() => setExpanded(isOpen ? null : dateStr)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                    <ChefHat className="w-4 h-4 text-brand-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 text-sm">
                      {format(new Date(dateStr), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
                    </div>
                    <div className="text-xs text-gray-400">
                      {dayLogs.length} tétel · összesen {totalQty} {dayLogs[0]?.unit || 'db'}
                    </div>
                  </div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {isOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {dayLogs.map((log: any) => (
                    <div key={log.id} className="px-4 py-3 flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-800 text-sm">{log.recipe_name}</span>
                          <span className="text-sm text-brand-600 font-semibold">{log.quantity} {log.unit}</span>
                          {log.production_time && <span className="text-xs text-gray-400">{log.production_time}</span>}
                          {log.batch_number && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono">
                              #{log.batch_number}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          {log.baking_temp && <span>Sütés: {log.baking_temp}°C</span>}
                          {log.cooling_temp && <span>Hűtés: {log.cooling_temp}°C</span>}
                          {log.operator_name && <span>Kezelő: {log.operator_name}</span>}
                        </div>
                        {log.notes && <div className="text-xs text-gray-400 mt-1">{log.notes}</div>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <KitchenLabelButton source="production" id={log.id} />
                        <button onClick={() => deleteMut.mutate(log.id)} className="text-gray-300 hover:text-red-500 shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Pultnapló tab ────────────────────────────────────────────────────────────
// Helyi idő "YYYY-MM-DDTHH:MM" formában (datetime-local mezőhöz és a backendnek)
const localNow = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}
const fmtDT = (s?: string | null) => (s ? format(new Date(s), 'MMM d. HH:mm', { locale: hu }) : '—')

const REMOVAL_LABEL: Record<string, string> = { eladva: 'Eladva', selejt: 'Selejt', egyeb: 'Egyéb' }
const REMOVAL_COLOR: Record<string, string> = {
  eladva: 'bg-green-100 text-green-700',
  selejt: 'bg-red-100 text-red-700',
  egyeb: 'bg-gray-100 text-gray-600',
}

function sellByStatus(sellBy?: string | null): 'expired' | 'today' | 'ok' | null {
  if (!sellBy) return null
  const t = new Date(sellBy).getTime()
  const now = Date.now()
  if (t < now) return 'expired'
  if (new Date(sellBy).toDateString() === new Date().toDateString()) return 'today'
  return 'ok'
}

function DisplayTab() {
  const qc = useQueryClient()
  const [showNew, setShowNew] = useState(false)
  const [removing, setRemoving] = useState<{ id: number; reason: string } | null>(null)
  const [removeNote, setRemoveNote] = useState('')
  const [removedBy, setRemovedBy] = useState('')
  const emptyForm = () => ({
    production_log_id: null as number | null,
    recipe_id: null as number | null,
    product_name: '',
    quantity: '' as string | number,
    unit: 'db',
    placed_at: localNow(),
    sell_by: '',
    placed_by: '',
    notes: '',
  })
  const [form, setForm] = useState(emptyForm())

  const { data: active = [] } = useQuery({
    queryKey: ['display-logs', 'active'],
    queryFn: () => getDisplayLogs({ active_only: true }).then(r => r.data),
    refetchInterval: 60000,
  })
  const since = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10)
  const { data: recent = [] } = useQuery({
    queryKey: ['display-logs', 'recent'],
    queryFn: () => getDisplayLogs({ from_date: since }).then(r => r.data),
  })
  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => getRecipes().then(r => r.data),
  })
  const { data: productions = [] } = useQuery({
    queryKey: ['production-logs', 'recent'],
    queryFn: () => getProductionLogs({ from_date: new Date(Date.now() - 3 * 86400000).toISOString().slice(0, 10) }).then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['display-logs'] })

  const createMut = useMutation({
    mutationFn: () => createDisplayLog({
      ...form,
      quantity: +form.quantity || 0,
      sell_by: form.sell_by || null,
    }),
    onSuccess: () => {
      refresh()
      toast.success('Pultba helyezve')
      setForm(emptyForm())
      setShowNew(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Hiba'),
  })

  const removeMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      removeDisplayLog(id, { reason, note: removeNote || null, removed_at: localNow(), removed_by: removedBy || null }),
    onSuccess: (_, v) => {
      refresh()
      toast.success(`Leszedve — ${REMOVAL_LABEL[v.reason]}`)
      setRemoving(null); setRemoveNote(''); setRemovedBy('')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Hiba'),
  })

  const undoMut = useMutation({
    mutationFn: (id: number) => undoRemoveDisplayLog(id),
    onSuccess: () => { refresh(); toast.success('Visszaállítva a pultba') },
  })
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteDisplayLog(id),
    onSuccess: () => { refresh(); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  function selectRecipe(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = +e.target.value || null
    const recipe = (recipes as any[]).find(r => r.id === id)
    setForm(f => ({ ...f, recipe_id: id, production_log_id: null, product_name: recipe?.name || '' }))
  }

  function selectProduction(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = +e.target.value || null
    const p = (productions as any[]).find(x => x.id === id)
    setForm(f => p
      ? { ...f, production_log_id: id, recipe_id: p.recipe_id ?? null, product_name: p.recipe_name, quantity: p.quantity, unit: p.unit || 'db' }
      : { ...f, production_log_id: null })
  }

  const selectedRecipe = (recipes as any[]).find(r => r.id === form.recipe_id)
  const expiredCount = (active as any[]).filter(i => sellByStatus(i.sell_by) === 'expired').length
  const history = (recent as any[]).filter(i => i.removed_at)
  const groupedHistory = history.reduce((acc: Record<string, any[]>, i: any) => {
    const d = i.placed_at.slice(0, 10)
    ;(acc[d] ||= []).push(i)
    return acc
  }, {})

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <p className="text-sm text-gray-500">Mi került a pultba, meddig árusítható, és mikor lett leszedve</p>
        <button className="btn-primary flex items-center gap-2 text-sm shrink-0" onClick={() => setShowNew(v => !v)}>
          <Plus className="w-4 h-4" /> Pultba helyezés
        </button>
      </div>

      {expiredCount > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-4 text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span><strong>{expiredCount} termék</strong> árusítási ideje lejárt, de még a pultban van — szedd le, és rögzítsd selejtként.</span>
        </div>
      )}

      {showNew && (
        <div className="card p-5 mb-5 border-brand-200">
          <h3 className="font-semibold text-gray-800 mb-4">Pultba helyezés</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            {(productions as any[]).length > 0 && (
              <div className="sm:col-span-2">
                <label className="label">Átvétel a termelési naplóból</label>
                <select className="input" value={form.production_log_id || ''} onChange={selectProduction}>
                  <option value="">— Nem a termelési naplóból —</option>
                  {(productions as any[]).map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {format(new Date(p.log_date), 'MMM d.', { locale: hu })} · {p.recipe_name} · {p.quantity} {p.unit}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="label">Termék *</label>
              <select className="input mb-2" value={form.recipe_id || ''} onChange={selectRecipe}>
                <option value="">— Válassz receptet —</option>
                {(recipes as any[]).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <input className="input" placeholder="Vagy írd be kézzel..." value={form.product_name}
                onChange={e => setForm(f => ({ ...f, product_name: e.target.value, recipe_id: null }))} />
            </div>
            <div>
              <label className="label">Mennyiség *</label>
              <input className="input" type="number" step="0.1" value={form.quantity} onChange={set('quantity')} />
            </div>
            <div>
              <label className="label">Egység</label>
              <select className="input" value={form.unit} onChange={set('unit')}>
                <option value="db">db</option>
                <option value="szelet">szelet</option>
                <option value="kg">kg</option>
                <option value="tálca">tálca</option>
                <option value="adag">adag</option>
              </select>
            </div>
            <div>
              <label className="label">Pultba helyezés ideje *</label>
              <input className="input" type="datetime-local" value={form.placed_at} onChange={set('placed_at')} />
            </div>
            <div>
              <label className="label">Meddig árusítható</label>
              <input className="input" type="datetime-local" value={form.sell_by} onChange={set('sell_by')} />
              <p className="text-xs text-gray-400 mt-1">
                {selectedRecipe?.shelf_life_days
                  ? `Üresen hagyva: ${form.production_log_id ? 'a gyártástól' : 'a pultba helyezéstől'} + ${selectedRecipe.shelf_life_days} nap (a recept eltarthatósága)`
                  : 'Receptnél üresen hagyva a recept eltarthatóságából számoljuk'}
              </p>
            </div>
            <StaffSelect label="Ki tette ki" value={form.placed_by} onChange={v => setForm(f => ({ ...f, placed_by: v }))} />
            <div>
              <label className="label">Megjegyzés</label>
              <input className="input" value={form.notes} onChange={set('notes')} placeholder="pl. vitrin 2. polc" />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => createMut.mutate()}
              disabled={!form.product_name || !form.quantity || !form.placed_at || createMut.isPending}>Mentés</button>
            <button className="btn-secondary text-sm" onClick={() => setShowNew(false)}>Mégse</button>
          </div>
        </div>
      )}

      {/* Most a pultban */}
      <div className="flex items-center gap-2 mb-2">
        <Store className="w-4 h-4 text-amber-600" />
        <span className="font-semibold text-gray-800 text-sm">Most a pultban ({(active as any[]).length})</span>
      </div>
      <div className="space-y-2 mb-6">
        {(active as any[]).length === 0 && (
          <p className="text-gray-400 text-sm text-center py-8 card">Jelenleg nincs rögzített termék a pultban</p>
        )}
        {(active as any[]).map((i: any) => {
          const st = sellByStatus(i.sell_by)
          return (
            <div key={i.id} className={`card px-4 py-3 ${st === 'expired' ? 'border-red-300 bg-red-50/40' : st === 'today' ? 'border-amber-300' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800 text-sm">{i.product_name}</span>
                    <span className="text-sm text-brand-600 font-semibold">{i.quantity} {i.unit}</span>
                    {st === 'expired' && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Lejárt</span>}
                    {st === 'today' && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Ma lejár</span>}
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                    <span>Kitéve: {fmtDT(i.placed_at)}</span>
                    <span className={st === 'expired' ? 'text-red-600 font-medium' : ''}>Árusítható: {fmtDT(i.sell_by)}</span>
                    {i.placed_by && <span>Kitette: {i.placed_by}</span>}
                  </div>
                  {i.notes && <div className="text-xs text-gray-400 mt-1">{i.notes}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <KitchenLabelButton source="display" id={i.id} />
                  <button onClick={() => { if (confirm('Biztosan törlöd ezt a bejegyzést? (Ha leszedted a pultról, inkább a Leszedés gombot használd.)')) deleteMut.mutate(i.id) }}
                    className="text-gray-300 hover:text-red-500 shrink-0" title="Téves bejegyzés törlése">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {removing && removing.id === i.id ? (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-2">Leszedés oka: <strong>{REMOVAL_LABEL[removing.reason]}</strong></p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                    <input className="input text-sm" value={removeNote} onChange={e => setRemoveNote(e.target.value)}
                      placeholder={removing.reason === 'egyeb' ? 'Mi történt vele? (kötelező)' : 'Megjegyzés (nem kötelező)'} />
                    <StaffSelect label="" value={removedBy} onChange={setRemovedBy} />
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-primary text-xs" disabled={removeMut.isPending || (removing.reason === 'egyeb' && !removeNote.trim())}
                      onClick={() => removeMut.mutate(removing)}>Leszedés rögzítése</button>
                    <button className="btn-secondary text-xs" onClick={() => { setRemoving(null); setRemoveNote('') }}>Mégse</button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-400 mr-1">Leszedés:</span>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100"
                    onClick={() => setRemoving({ id: i.id, reason: 'eladva' })}>Eladva</button>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                    onClick={() => setRemoving({ id: i.id, reason: 'selejt' })}>Selejt</button>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                    onClick={() => setRemoving({ id: i.id, reason: 'egyeb' })}>Egyéb</button>
                </div>
              )}
            </div>
          )
        })}
      </div>
      <p className="text-xs text-gray-400 mb-6 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" /> Pultból levett terméket visszafagyasztani tilos.
      </p>

      {/* Napló — utolsó 14 nap */}
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle className="w-4 h-4 text-gray-400" />
        <span className="font-semibold text-gray-800 text-sm">Leszedett termékek — utolsó 14 nap</span>
      </div>
      <div className="space-y-3">
        {Object.keys(groupedHistory).length === 0 && (
          <p className="text-gray-400 text-sm text-center py-6">Még nincs leszedett termék</p>
        )}
        {Object.entries(groupedHistory).map(([d, items]) => (
          <div key={d} className="card overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 text-sm font-medium text-gray-700">
              {format(new Date(d), 'yyyy. MMMM d. (EEEE)', { locale: hu })}
            </div>
            <div className="divide-y divide-gray-50">
              {(items as any[]).map((i: any) => (
                <div key={i.id} className="px-4 py-2.5 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-800">{i.product_name}</span>
                      <span className="text-sm text-gray-500">{i.quantity} {i.unit}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${REMOVAL_COLOR[i.removal_reason] || ''}`}>
                        {REMOVAL_LABEL[i.removal_reason] || i.removal_reason}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap">
                      <span>Kitéve: {fmtDT(i.placed_at)}</span>
                      <span>Leszedve: {fmtDT(i.removed_at)}</span>
                      {i.removed_by && <span>Leszedte: {i.removed_by}</span>}
                    </div>
                    {i.removal_note && <div className="text-xs text-gray-400 mt-0.5">{i.removal_note}</div>}
                  </div>
                  <button onClick={() => undoMut.mutate(i.id)} className="text-gray-300 hover:text-brand-600 shrink-0" title="Téves leszedés visszavonása">
                    <Undo2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Fő oldal ─────────────────────────────────────────────────────────────────
export default function HaccpPage() {
  const [tab, setTab] = useState<Tab>('fridge')
  const navigate = useNavigate()

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })

  const isPaid = sub && sub.plan !== 'free'

  const tabs = [
    { key: 'fridge' as Tab, label: 'Hűtő / Fagyasztó', shortLabel: 'Hűtő', icon: Thermometer, color: 'text-blue-600' },
    { key: 'cleaning' as Tab, label: 'Takarítás', shortLabel: 'Takarítás', icon: CheckSquare, color: 'text-green-600' },
    { key: 'production' as Tab, label: 'Termelés', shortLabel: 'Termelés', icon: ChefHat, color: 'text-brand-600' },
    { key: 'display' as Tab, label: 'Pultnapló', shortLabel: 'Pult', icon: Store, color: 'text-amber-600' },
  ]

  if (!isPaid) {
    return (
      <div>
        <div className="mb-5">
          <h1 className="text-xl font-bold text-gray-900">HACCP Napló</h1>
          <p className="text-sm text-gray-500 mt-0.5">Digitális napi nyilvántartás — hőmérséklet, takarítás, termelés, pult</p>
        </div>
        <div className="rounded-2xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-white p-8 text-center max-w-lg mx-auto mt-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-100 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-brand-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Digitális HACCP naplók</h2>
          <p className="text-sm text-gray-600 mb-5">
            Hűtő/fagyasztó hőmérséklet napló, takarítási ellenőrzőlista, termelési napló és pultnapló — papírmentes, ellenőrzésre kész dokumentáció.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-left">
            {[
              { icon: Thermometer, title: 'Hűtő napló', desc: 'Eszközönkénti hőmérséklet, riasztások', color: 'bg-blue-100 text-blue-600' },
              { icon: CheckSquare, title: 'Takarítás', desc: 'Napi / heti / havi feladatlista', color: 'bg-green-100 text-green-600' },
              { icon: ChefHat, title: 'Termelés', desc: 'Mit, mikor, ki készített', color: 'bg-brand-100 text-brand-600' },
              { icon: Store, title: 'Pultnapló', desc: 'Mi van a pultban, meddig árusítható', color: 'bg-amber-100 text-amber-600' },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="rounded-xl bg-white border border-gray-100 p-3">
                <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center mb-2`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="font-semibold text-gray-800 text-xs mb-0.5">{title}</div>
                <div className="text-xs text-gray-400">{desc}</div>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/elofizetes')} className="btn-primary flex items-center gap-2 mx-auto">
            <Zap className="w-4 h-4" /> Alap csomag aktiválása
          </button>
          <p className="text-xs text-gray-400 mt-3">Alap: 2 990 Ft/hó · Pro: 5 990 Ft/hó</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">HACCP Napló</h1>
        <p className="text-sm text-gray-500 mt-0.5">Digitális napi nyilvántartás — hőmérséklet, takarítás, termelés, pult</p>
      </div>

      <div className="flex gap-0.5 border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map(({ key, label, shortLabel, icon: Icon, color }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === key ? 'border-brand-500 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className={`w-4 h-4 ${tab === key ? color : 'text-gray-400'}`} />
            <span className="sm:hidden">{shortLabel}</span>
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {tab === 'fridge' && <FridgeTab />}
      {tab === 'cleaning' && <CleaningTab />}
      {tab === 'production' && (
        <>
          <SemifinishedSection />
          <div className="border-t border-gray-100 pt-5 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <ChefHat className="w-4 h-4 text-brand-600" />
              <span className="font-semibold text-gray-800 text-sm">Termelési napló</span>
            </div>
            <p className="text-xs text-gray-400">Elkészült késztermékek napi rögzítése</p>
          </div>
          <ProductionTab />
        </>
      )}
      {tab === 'display' && <DisplayTab />}
    </div>
  )
}
